import React, { useState, useEffect } from "react";
import ModalComponent from "../Tenant/ModalComponent/ModalComponent";
import toast from "react-hot-toast";

const ExcelExportModal = ({
    show,
    setShow,
    columns,
    request,
    exportFunc,
    isLoading,
    title = "Export Data",
    filename = "ExportData",
    showFilters = false,
    filterConfig = []
}) => {
    const [selectedColumns, setSelectedColumns] = useState(
        columns ? columns.map((c) => c.name) : []
    );

    const [localRequest, setLocalRequest] = useState(request || {});

    // Sync localRequest with prop request when modal opens or request changes
    useEffect(() => {
        if (show) {
            setLocalRequest(request || {});
        }
    }, [show, request]);

    const handleToggleColumn = (columnName) => {
        if (selectedColumns.includes(columnName)) {
            setSelectedColumns(selectedColumns.filter((c) => c !== columnName));
        } else {
            setSelectedColumns([...selectedColumns, columnName]);
        }
    };

    const handleSelectAll = () => {
        setSelectedColumns(columns.map((c) => c.name));
    };

    const handleClearAll = () => {
        setSelectedColumns([]);
    };

    const handleFilterChange = (key, value) => {
        setLocalRequest(prev => ({ ...prev, [key]: value }));
    };

    const handleExport = async () => {
        if (selectedColumns.length === 0) {
            toast.error("Please select at least one column to export.");
            return;
        }

        try {
            const body = {
                ...localRequest,
                columns: selectedColumns,
                limit: null,
                offset: null,
            };

            const response = await exportFunc({
                token: sessionStorage.getItem("access_token"),
                tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
                body: body,
            }).unwrap();

            if (response && response.body && (response.body.fileLink || response.body.link)) {
                const link = response.body.fileLink || response.body.link;
                const a = document.createElement("a");
                a.href = link;
                a.download = `${filename}+${new Date().getTime()}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                toast.success("Export successful!");
                setShow(false);
            } else {
                toast.error("Failed to generate export link.");
            }
        } catch (error) {
            console.error("Export error:", error);
            toast.error(error?.data?.message || "An error occurred during export.");
        }
    };

    return (
        <ModalComponent show={show} setShow={setShow} size="4xl" showClose={true}>
            <div className="p-6">
                <h3 className="text-xl font-bold mb-4 text-center">{title}</h3>

                {showFilters && filterConfig.length > 0 && (
                    <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h4 className="text-sm font-semibold mb-3 border-b border-gray-300 pb-1 text-gray-700">Search Filters</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {filterConfig.map((filter) => (
                                <div key={filter.key} className="flex flex-col">
                                    <label className="block text-xs font-semibold capitalize leading-6 text-gray-700 mb-0.5">
                                        {filter.label}
                                    </label>
                                    <input
                                        type={filter.type || "text"}
                                        className="w-full px-2 py-1.5 text-xs text-gray-900 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 bg-white"
                                        placeholder={`${filter.label}...`}
                                        value={localRequest[filter.key] || ""}
                                        onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-medium text-gray-700">
                        Select columns to include in the export:
                    </span>
                    <div className="space-x-2">
                        <button
                            type="button"
                            onClick={handleSelectAll}
                            className="text-xs text-blue-600 hover:underline"
                        >
                            Select All
                        </button>
                        <button
                            type="button"
                            onClick={handleClearAll}
                            className="text-xs text-red-600 hover:underline"
                        >
                            Clear All
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-h-60 overflow-y-auto border border-gray-200 p-3 rounded-lg bg-gray-50 mb-6 font-sans">
                    {columns && columns.map((column) => (
                        <label
                            key={column.name}
                            className="flex items-center space-x-2 cursor-pointer hover:bg-white p-1 rounded transition-colors mr-2"
                        >
                            <input
                                type="checkbox"
                                checked={selectedColumns.includes(column.name)}
                                onChange={() => handleToggleColumn(column.name)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-800">{column.name}</span>
                        </label>
                    ))}
                </div>

                <div className="flex justify-center space-x-4">
                    <button
                        onClick={() => setShow(false)}
                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={isLoading}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:bg-blue-400"
                    >
                        {isLoading ? (
                            <>
                                <svg
                                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                Exporting...
                            </>
                        ) : (
                            "Download Excel"
                        )}
                    </button>
                </div>
            </div>
        </ModalComponent>
    );
};

export default ExcelExportModal;
